// WebSIG Geral — todas geometrias de todos os contratos num mapa único.
// Espelha o padrão do MapaContrato (MapLibre + add/removeCamada) mas:
//   - Lê via op_listar_geometrias_todos_contratos (handler geometriasGerais.js)
//   - Agrupa por contrato na sidebar
//   - Filtros server-side: cliente, status_contrato, tipo_levantamento, intervalo data
//   - Cada feature popup mostra contrato + cliente
//   - Read-only: sem upload/desenho/edição (isso fica no MapaContrato individual)
// Wave 6 (Antd) — 2026-06-01. Migrado de Mantine pra Antd. Lógica do mapa intacta.
import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Button, Tag, Typography, Card, Select, DatePicker, Tooltip, Spin, Space, Flex,
  App as AntApp,
} from 'antd';
import {
  ReloadOutlined, FilterOutlined, DownOutlined, RightOutlined,
  EyeOutlined, EyeInvisibleOutlined, EnvironmentOutlined, GlobalOutlined,
  DownloadOutlined, CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../config';
import { fmtArea, fmtDistancia } from '../lib/geoParse';
import type { FeatureCollection } from 'geojson';

const { Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface GeometriaConsolidada {
  id: string;
  contrato_id: string;
  nome: string;
  tipo: string;
  tipo_levantamento: string | null;
  geojson: FeatureCollection;
  area_m2: number | null;
  perimetro_m: number | null;
  comprimento_m: number | null;
  bbox: [number, number, number, number] | null;
  n_features: number | null;
  cor: string;
  visivel: boolean;
  observacoes: string | null;
  arquivo_origem_nome: string | null;
  criado_em: string;
  criado_por: string | null;
  contrato_numero: string;
  contrato_titulo: string;
  contrato_status: string;
  contrato_cliente_id: number | null;
  contrato_responsavel_email: string | null;
  data_inicio_real: string | null;
  data_termino_real: string | null;
  cliente_razao_social: string | null;
  cliente_nome_fantasia: string | null;
}

interface OpcoesFiltro {
  tipos_levantamento: string[];
  statuses_contrato: string[];
  clientes: { id: number; razao_social: string; nome_fantasia: string | null }[];
}

interface ContratoGrupo {
  contrato_id: string;
  contrato_numero: string;
  contrato_titulo: string;
  contrato_status: string;
  cliente_label: string;
  geometrias: GeometriaConsolidada[];
}

function estiloOsm(): any {
  return {
    version: 8,
    sources: {
      osm: {
        type: 'raster', tileSize: 256,
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        attribution: '© OpenStreetMap',
      },
    },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
  };
}

function estiloSatelite(): any {
  return {
    version: 8,
    sources: {
      esri: {
        type: 'raster', tileSize: 256,
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
        attribution: '© Esri',
      },
    },
    layers: [{ id: 'esri', type: 'raster', source: 'esri' }],
  };
}

function mergeBoundsArray(bboxes: [number, number, number, number][]): [number, number, number, number] {
  return bboxes.reduce<[number, number, number, number]>(
    (acc, b) => [
      Math.min(acc[0], b[0]), Math.min(acc[1], b[1]),
      Math.max(acc[2], b[2]), Math.max(acc[3], b[3]),
    ],
    [180, 90, -180, -90]
  );
}

function statusCor(status: string): string {
  const m: Record<string, string> = {
    planejamento: '#6b7280', em_andamento: '#0930D0', concluido: '#0a7f4a',
    cancelado: '#9ca3af', suspenso: '#B07500',
  };
  return m[status] || '#444';
}

export default function OperacionalMapaGeral() {
  return <AntApp><OperacionalMapaGeralInner /></AntApp>;
}

function OperacionalMapaGeralInner() {
  const { message } = AntApp.useApp();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const camadasIdsRef = useRef<Set<string>>(new Set());

  const [geometrias, setGeometrias] = useState<GeometriaConsolidada[]>([]);
  const [opcoes, setOpcoes] = useState<OpcoesFiltro | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [estiloBase, setEstiloBase] = useState<'osm' | 'satelite'>('osm');
  const [contratosCollapsed, setContratosCollapsed] = useState<Set<string>>(new Set());
  const [visiveisOverride, setVisiveisOverride] = useState<Record<string, boolean>>({});
  const [popupFeature, setPopupFeature] = useState<any | null>(null);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const [filtroClienteId, setFiltroClienteId] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string[]>([]);
  const [filtroTipos, setFiltroTipos] = useState<string[]>([]);
  const [filtroDataDe, setFiltroDataDe] = useState<string>('');
  const [filtroDataAte, setFiltroDataAte] = useState<string>('');

  // ── Inicializa mapa ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: estiloBase === 'satelite' ? estiloSatelite() : estiloOsm(),
      center: [-51.93, -30.03],
      zoom: 5,
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      camadasIdsRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Troca de estilo base mantém camadas ───────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const novo = estiloBase === 'satelite' ? estiloSatelite() : estiloOsm();
    map.setStyle(novo);
    map.once('styledata', () => {
      camadasIdsRef.current.clear();
      for (const g of geometrias) {
        if (geomVisivel(g)) adicionarCamadaNoMapa(g);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estiloBase]);

  // ── Carrega opções de filtro + geometrias ─────────────────────────────────
  useEffect(() => { carregarOpcoes(); carregarGeometrias(); /* eslint-disable-next-line */ }, []);

  async function carregarOpcoes() {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_listar_opcoes_filtro_mapa_geral' },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setOpcoes(d);
    } catch (e: any) {
      console.warn('Falha carregando opções de filtro', e);
    }
  }

  async function carregarGeometrias() {
    setLoading(true);
    setErro(null);
    try {
      const token = await getToken();
      const body: any = { action: 'op_listar_geometrias_todos_contratos' };
      if (filtroClienteId) body.cliente_id = parseInt(filtroClienteId, 10);
      if (filtroStatus.length) body.status_contrato = filtroStatus;
      if (filtroTipos.length) body.tipo_levantamento = filtroTipos;
      if (filtroDataDe) body.data_de = filtroDataDe;
      if (filtroDataAte) body.data_ate = filtroDataAte;
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body,
        { headers: { Authorization: token }, timeout: 60000 });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      const lista: GeometriaConsolidada[] = d.geometrias || [];
      setGeometrias(lista);
      setVisiveisOverride({});
      const map = mapRef.current;
      if (map) {
        camadasIdsRef.current.forEach(id => removerCamadaDoMapa(id));
        camadasIdsRef.current.clear();
        for (const g of lista) if (g.visivel) adicionarCamadaNoMapa(g);
        const bboxes = lista.filter(g => g.visivel && g.bbox).map(g => g.bbox!);
        if (bboxes.length) {
          const merged = mergeBoundsArray(bboxes);
          map.fitBounds([[merged[0], merged[1]], [merged[2], merged[3]]],
            { padding: 50, duration: 600, maxZoom: 14 });
        }
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || String(e);
      setErro(`Falha carregando geometrias: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  function geomVisivel(g: GeometriaConsolidada): boolean {
    if (visiveisOverride[g.id] !== undefined) return visiveisOverride[g.id];
    return g.visivel !== false;
  }

  function adicionarCamadaNoMapa(g: GeometriaConsolidada) {
    const map = mapRef.current;
    if (!map) return;
    if (camadasIdsRef.current.has(g.id)) return;
    try {
      map.addSource(g.id, { type: 'geojson', data: g.geojson as any });
      map.addLayer({
        id: `${g.id}-fill`, source: g.id, type: 'fill',
        paint: { 'fill-color': g.cor || '#0930D0', 'fill-opacity': 0.25 },
        filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
      });
      map.addLayer({
        id: `${g.id}-line`, source: g.id, type: 'line',
        paint: { 'line-color': g.cor || '#0930D0', 'line-width': 2.5 },
      });
      map.addLayer({
        id: `${g.id}-point`, source: g.id, type: 'circle',
        paint: {
          'circle-radius': 5, 'circle-color': g.cor || '#0930D0',
          'circle-stroke-color': '#fff', 'circle-stroke-width': 1.5,
        },
        filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']],
      });
      const aoClicar = (e: maplibregl.MapLayerMouseEvent) => {
        const f = e.features?.[0];
        if (!f) return;
        setPopupFeature({ ...f, _geom: g });
      };
      const setCursor = () => { map.getCanvas().style.cursor = 'pointer'; };
      const unsetCursor = () => { map.getCanvas().style.cursor = ''; };
      [`${g.id}-fill`, `${g.id}-line`, `${g.id}-point`].forEach(id => {
        map.on('click', id, aoClicar);
        map.on('mouseenter', id, setCursor);
        map.on('mouseleave', id, unsetCursor);
      });
      camadasIdsRef.current.add(g.id);
    } catch (e) {
      console.error(`Erro adicionando camada ${g.nome}:`, e);
    }
  }

  function removerCamadaDoMapa(id: string) {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (map.getLayer(`${id}-fill`)) map.removeLayer(`${id}-fill`);
      if (map.getLayer(`${id}-line`)) map.removeLayer(`${id}-line`);
      if (map.getLayer(`${id}-point`)) map.removeLayer(`${id}-point`);
      if (map.getSource(id)) map.removeSource(id);
      camadasIdsRef.current.delete(id);
    } catch {/* ignora */}
  }

  function toggleGeometriaVisivel(g: GeometriaConsolidada) {
    const novoVisivel = !geomVisivel(g);
    setVisiveisOverride(prev => ({ ...prev, [g.id]: novoVisivel }));
    if (novoVisivel) adicionarCamadaNoMapa(g);
    else removerCamadaDoMapa(g.id);
  }

  function toggleContrato(contratoId: string) {
    setContratosCollapsed(prev => {
      const n = new Set(prev);
      if (n.has(contratoId)) n.delete(contratoId); else n.add(contratoId);
      return n;
    });
  }

  function centralizarGeometria(g: GeometriaConsolidada) {
    const map = mapRef.current;
    if (!map || !g.bbox) return;
    map.fitBounds([[g.bbox[0], g.bbox[1]], [g.bbox[2], g.bbox[3]]],
      { padding: 60, duration: 600, maxZoom: 17 });
  }

  function centralizarContrato(grupo: ContratoGrupo) {
    const map = mapRef.current;
    if (!map) return;
    const bboxes = grupo.geometrias.filter(g => g.bbox).map(g => g.bbox!);
    if (!bboxes.length) return;
    const merged = mergeBoundsArray(bboxes);
    map.fitBounds([[merged[0], merged[1]], [merged[2], merged[3]]],
      { padding: 60, duration: 600, maxZoom: 16 });
  }

  function centralizarTudo() {
    const map = mapRef.current;
    if (!map) return;
    const bboxes = geometrias.filter(g => geomVisivel(g) && g.bbox).map(g => g.bbox!);
    if (!bboxes.length) return;
    const merged = mergeBoundsArray(bboxes);
    map.fitBounds([[merged[0], merged[1]], [merged[2], merged[3]]],
      { padding: 50, duration: 600, maxZoom: 14 });
  }

  function exportarGeoJsonConsolidado() {
    const features = geometrias.flatMap(g => (g.geojson?.features || []).map(f => ({
      ...f,
      properties: {
        ...(f.properties || {}),
        _camada: g.nome,
        _contrato: g.contrato_numero,
        _titulo: g.contrato_titulo,
        _cliente: g.cliente_nome_fantasia || g.cliente_razao_social,
        _tipo_levantamento: g.tipo_levantamento,
      },
    })));
    const fc = { type: 'FeatureCollection', features };
    const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mapa-geral-hydrodata-${new Date().toISOString().slice(0, 10)}.geojson`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    message.success('GeoJSON exportado.');
  }

  // ── Agrupamento por contrato ──────────────────────────────────────────────
  const grupos: ContratoGrupo[] = useMemo(() => {
    const m = new Map<string, ContratoGrupo>();
    for (const g of geometrias) {
      if (!m.has(g.contrato_id)) {
        m.set(g.contrato_id, {
          contrato_id: g.contrato_id,
          contrato_numero: g.contrato_numero,
          contrato_titulo: g.contrato_titulo,
          contrato_status: g.contrato_status,
          cliente_label: g.cliente_nome_fantasia || g.cliente_razao_social || '—',
          geometrias: [],
        });
      }
      m.get(g.contrato_id)!.geometrias.push(g);
    }
    return Array.from(m.values());
  }, [geometrias]);

  const stats = useMemo(() => {
    const visiveis = geometrias.filter(geomVisivel);
    return {
      totalGeom: geometrias.length,
      visiveis: visiveis.length,
      totalContratos: grupos.length,
      areaTotal: visiveis.reduce((s, g) => s + (g.area_m2 || 0), 0),
      comprTotal: visiveis.reduce((s, g) => s + (g.comprimento_m || 0), 0),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometrias, visiveisOverride, grupos]);

  return (
    <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column',
      gap: 12, height: 'calc(100vh - 80px)' }}>
      {/* Header */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={8}
        style={{ paddingBottom: 10, borderBottom: '0.5px solid #e0e0e0' }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0, color: '#020D7F' }}>
            🗺️ Mapa Geral — Todos os Contratos
          </Typography.Title>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Histórico institucional de levantamentos e camadas de contrato
          </Text>
        </div>
        <Space size={4}>
          <Tooltip title="Atualizar">
            <Button type="text" icon={<ReloadOutlined />} onClick={carregarGeometrias} />
          </Tooltip>
          <Tooltip title="Centralizar tudo">
            <Button type="text" icon={<EnvironmentOutlined />} onClick={centralizarTudo} />
          </Tooltip>
          <Tooltip title="Exportar GeoJSON consolidado">
            <Button type="text" icon={<DownloadOutlined />} onClick={exportarGeoJsonConsolidado} />
          </Tooltip>
          <Tooltip title={`Estilo base: ${estiloBase === 'osm' ? 'OSM' : 'Satélite'}`}>
            <Button type="text" icon={<GlobalOutlined />}
              onClick={() => setEstiloBase(s => s === 'osm' ? 'satelite' : 'osm')} />
          </Tooltip>
          <Button size="small" icon={<FilterOutlined />}
            type={filtrosAbertos ? 'primary' : 'default'}
            onClick={() => setFiltrosAbertos(f => !f)}>
            Filtros
          </Button>
        </Space>
      </Flex>

      {/* Painel de filtros */}
      {filtrosAbertos && (
        <Card size="small">
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 4 }}>Cliente</Text>
                <Select placeholder="Todos" allowClear showSearch optionFilterProp="label"
                  value={filtroClienteId} onChange={(v) => setFiltroClienteId(v || null)}
                  style={{ width: '100%' }}
                  options={(opcoes?.clientes || []).map(c => ({
                    value: String(c.id), label: c.nome_fantasia || c.razao_social,
                  }))} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 4 }}>Status do contrato</Text>
                <Select mode="multiple" placeholder="Todos"
                  value={filtroStatus} onChange={setFiltroStatus}
                  style={{ width: '100%' }} maxTagCount="responsive"
                  options={(opcoes?.statuses_contrato || []).map(s => ({ value: s, label: s }))} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 4 }}>Tipo de levantamento</Text>
                <Select mode="multiple" placeholder="Todos"
                  value={filtroTipos} onChange={setFiltroTipos}
                  style={{ width: '100%' }} maxTagCount="responsive"
                  options={[
                    ...(opcoes?.tipos_levantamento || []).map(t => ({ value: t, label: t })),
                    { value: 'sem_tipo', label: '(sem tipo)' },
                  ]} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
              <div>
                <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 4 }}>Criado de</Text>
                <DatePicker value={filtroDataDe ? dayjs(filtroDataDe) : null}
                  onChange={(d) => setFiltroDataDe(d ? d.format('YYYY-MM-DD') : '')}
                  format="DD/MM/YYYY" style={{ width: '100%' }} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 4 }}>Criado até</Text>
                <DatePicker value={filtroDataAte ? dayjs(filtroDataAte) : null}
                  onChange={(d) => setFiltroDataAte(d ? d.format('YYYY-MM-DD') : '')}
                  format="DD/MM/YYYY" style={{ width: '100%' }} />
              </div>
              <Space size={8}>
                <Button type="primary" onClick={carregarGeometrias} loading={loading}>Aplicar</Button>
                <Button type="text" onClick={() => {
                  setFiltroClienteId(null); setFiltroStatus([]); setFiltroTipos([]);
                  setFiltroDataDe(''); setFiltroDataAte('');
                  setTimeout(carregarGeometrias, 0);
                }}>Limpar</Button>
              </Space>
            </div>
          </Space>
        </Card>
      )}

      {erro && (
        <Card size="small" style={{ background: '#FEE2E2', borderColor: '#FCA5A5' }}
          styles={{ body: { padding: '6px 12px' } }}>
          <Text style={{ color: '#991B1B', fontSize: 12 }}>{erro}</Text>
        </Card>
      )}

      {/* Conteúdo: sidebar + mapa */}
      <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
        {/* Sidebar esquerda */}
        <Card size="small" style={{ width: 340, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          styles={{ body: { padding: 8, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } }}>
          {/* Stats */}
          <Space direction="vertical" size={4} style={{ marginBottom: 8 }}>
            <Space size={4} wrap>
              <Tag color="blue">{stats.totalContratos} contratos</Tag>
              <Tag color="purple">{stats.visiveis}/{stats.totalGeom} camadas</Tag>
            </Space>
            <Space size={8}>
              <Text type="secondary" style={{ fontSize: 11 }}>Área: <strong>{fmtArea(stats.areaTotal)}</strong></Text>
              <Text type="secondary" style={{ fontSize: 11 }}>Compr: <strong>{fmtDistancia(stats.comprTotal)}</strong></Text>
            </Space>
          </Space>

          {/* Lista agrupada */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {loading && <Flex justify="center" style={{ padding: 16 }}><Spin /></Flex>}
            {!loading && grupos.length === 0 && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center', padding: 16 }}>
                Nenhuma geometria encontrada com os filtros atuais.
              </Text>
            )}
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {grupos.map(grupo => {
                const collapsed = contratosCollapsed.has(grupo.contrato_id);
                return (
                  <div key={grupo.contrato_id} style={{ border: '0.5px solid #e0e0e0', borderRadius: 4 }}>
                    <UnstyledGroupRow
                      onToggle={() => toggleContrato(grupo.contrato_id)}
                      onCentralizar={() => centralizarContrato(grupo)}
                      collapsed={collapsed}
                      grupo={grupo}
                    />
                    {!collapsed && (
                      <Space direction="vertical" size={2}
                        style={{ width: '100%', padding: 6, borderTop: '0.5px solid #f0f0f0' }}>
                        {grupo.geometrias.map(g => (
                          <CamadaRow key={g.id} g={g} visivel={geomVisivel(g)}
                            onToggle={() => toggleGeometriaVisivel(g)}
                            onCentralizar={() => centralizarGeometria(g)} />
                        ))}
                      </Space>
                    )}
                  </div>
                );
              })}
            </Space>
          </div>
        </Card>

        {/* Mapa */}
        <div style={{ flex: 1, position: 'relative', borderRadius: 6, overflow: 'hidden',
          border: '0.5px solid #e0e0e0' }}>
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
          {popupFeature && (
            <Card size="small" style={{
              position: 'absolute', top: 12, right: 12, width: 320, zIndex: 5,
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            }}>
              <Flex justify="space-between" align="start" style={{ marginBottom: 4 }}>
                <Text strong style={{ fontSize: 13 }}>{popupFeature._geom?.nome}</Text>
                <Button type="text" size="small" icon={<CloseOutlined />}
                  onClick={() => setPopupFeature(null)} />
              </Flex>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                Contrato: <strong>{popupFeature._geom?.contrato_numero}</strong> — {popupFeature._geom?.contrato_titulo}
              </Text>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                Cliente: {popupFeature._geom?.cliente_nome_fantasia || popupFeature._geom?.cliente_razao_social || '—'}
              </Text>
              {popupFeature._geom?.tipo_levantamento && (
                <Tag color="blue" style={{ fontSize: 10, marginTop: 4 }}>
                  {popupFeature._geom.tipo_levantamento}
                </Tag>
              )}
              {popupFeature._geom?.area_m2 && (
                <Text style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                  Área (camada): <strong>{fmtArea(popupFeature._geom.area_m2)}</strong>
                </Text>
              )}
              {popupFeature._geom?.comprimento_m && (
                <Text style={{ fontSize: 11, display: 'block' }}>
                  Comprimento (camada): <strong>{fmtDistancia(popupFeature._geom.comprimento_m)}</strong>
                </Text>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Subcomponentes ─────────────────────────────────────────────────────────

function UnstyledGroupRow({ grupo, collapsed, onToggle, onCentralizar }: {
  grupo: ContratoGrupo; collapsed: boolean; onToggle: () => void; onCentralizar: () => void;
}) {
  return (
    <Flex gap={4} align="center" wrap="nowrap" style={{ padding: 6, cursor: 'pointer' }} onClick={onToggle}>
      <Button type="text" size="small"
        icon={collapsed ? <RightOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ fontSize: 11, display: 'block', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {grupo.contrato_numero} — {grupo.contrato_titulo}
        </Text>
        <Flex gap={4} align="center" wrap="nowrap">
          <Text type="secondary" style={{ fontSize: 10, flex: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {grupo.cliente_label}
          </Text>
          <Tag style={{ fontSize: 9, padding: '0 4px', margin: 0,
            background: statusCor(grupo.contrato_status) + '22',
            color: statusCor(grupo.contrato_status),
            border: 'none' }}>
            {grupo.contrato_status}
          </Tag>
          <Tag style={{ fontSize: 9, padding: '0 4px', margin: 0 }}>
            {grupo.geometrias.length}
          </Tag>
        </Flex>
      </div>
      <Tooltip title="Centralizar no mapa">
        <Button type="text" size="small" icon={<EnvironmentOutlined style={{ fontSize: 11 }} />}
          onClick={(e) => { e.stopPropagation(); onCentralizar(); }} />
      </Tooltip>
    </Flex>
  );
}

function CamadaRow({ g, visivel, onToggle, onCentralizar }: {
  g: GeometriaConsolidada; visivel: boolean; onToggle: () => void; onCentralizar: () => void;
}) {
  return (
    <Flex gap={4} align="center" wrap="nowrap" style={{ padding: '2px 4px' }}>
      <div style={{ width: 10, height: 10, borderRadius: 2, background: g.cor, flexShrink: 0 }} />
      <Text style={{ fontSize: 11, flex: 1, minWidth: 0, whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {g.nome}
      </Text>
      {g.tipo_levantamento && (
        <Tag style={{ fontSize: 9, padding: '0 4px', margin: 0 }}>{g.tipo_levantamento}</Tag>
      )}
      <Tooltip title="Centralizar">
        <Button type="text" size="small" icon={<EnvironmentOutlined style={{ fontSize: 10 }} />}
          onClick={onCentralizar} />
      </Tooltip>
      <Tooltip title={visivel ? 'Ocultar' : 'Mostrar'}>
        <Button type="text" size="small"
          icon={visivel ? <EyeOutlined style={{ fontSize: 10 }} /> : <EyeInvisibleOutlined style={{ fontSize: 10 }} />}
          onClick={onToggle} />
      </Tooltip>
    </Flex>
  );
}
